import React from 'react';
import { Box, Flex, TextV2, ButtonV2, CloseButton } from '@withjoy/joykit';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import type { WebsiteBannerProps } from './WebsiteBanner.types';
import { pxToRem } from '@withjoy/joykit/theme';
import { WEBSITE_BANNER_HEIGHT_DESKTOP, WEBSITE_BANNER_HEIGHT_MOBILE } from './WebsiteBanner.constants';

const WebsiteBanner: React.FC<WebsiteBannerProps> = ({ message, actionButton, onClose, closeButtonPlacement = 'right' }) => {
  const isMobile = useIsMobileScreen();

  const displayMessage = (isMobile && message.mobile) || message.default;
  const displayButtonLabel = isMobile && actionButton?.label.mobile ? actionButton.label.mobile : actionButton?.label.default;

  const closeOnLeft = closeButtonPlacement === 'left';

  const messageNode = (
    <TextV2
      typographyVariant={{ _: 'label2', md: 'label3' }}
      color="white"
    >
      {displayMessage}
    </TextV2>
  );

  const actionButtonNode = actionButton && (
    <ButtonV2
      flexShrink={0}
      variant="outline"
      shape="rounded"
      typographyVariant={{ _: 'button1', md: 'button2' }}
      intent="primary"
      _hover={{ color: 'white', borderColor: 'white' }}
      _active={{ color: 'white', borderColor: 'white' }}
      height="fit-content"
      onClick={actionButton?.onClick}
      paddingX={{ _: 4, md: 5 }}
      paddingY={{ _: pxToRem(7), md: pxToRem(10) }}
      color="white"
      borderColor="white"
      loading={actionButton?.loading}
    >
      {displayButtonLabel}
    </ButtonV2>
  );

  // Left placement: close → text → button, anchored to the left. The close button uses `size="md"`,
  // whose 16px (token 5) horizontal padding does double duty against a zero left gutter: its left
  // padding drops the icon at the banner's 16px edge — matching the guest-site hamburger menu — and
  // its right padding sets the same 16px to the text. Padding (not a smaller box) handles alignment,
  // so the full md tap target is preserved for this touch-only banner.
  if (closeOnLeft) {
    return (
      <Box
        display="flex"
        backgroundColor="newUIBrandPrimary"
        width="100%"
        zIndex="banner"
        position="fixed"
        top={0}
        alignItems="center"
        justifyContent="space-between"
        paddingLeft={0}
        paddingRight={5}
        height={{ _: WEBSITE_BANNER_HEIGHT_MOBILE, md: WEBSITE_BANNER_HEIGHT_DESKTOP }}
        paddingY={5}
        gap={4}
      >
        <Flex
          alignItems="center"
          minWidth={0}
        >
          {onClose && (
            <CloseButton
              flexShrink={0}
              size="md"
              color="white"
              onClick={onClose}
            />
          )}
          {messageNode}
        </Flex>
        {actionButtonNode}
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      backgroundColor="newUIBrandPrimary"
      width="100%"
      zIndex="banner"
      position="fixed"
      top={0}
      alignItems="center"
      justifyContent="center"
      paddingLeft={5}
      height={{ _: WEBSITE_BANNER_HEIGHT_MOBILE, md: WEBSITE_BANNER_HEIGHT_DESKTOP }}
      paddingRight={{ _: 7, md: 5 }}
      paddingY={5}
      gap={{ _: 2, md: 4 }}
    >
      {messageNode}
      {actionButtonNode}
      {onClose && (
        <CloseButton
          position="absolute"
          color="white"
          right={{ _: -16, md: 6 }}
          onClick={onClose}
        />
      )}
    </Box>
  );
};

WebsiteBanner.displayName = 'WebsiteBanner';

export { WebsiteBanner };
